<script setup lang="ts">
import { useRoute } from 'vue-router'
import type { ProjectGalleryItem, ProjectTeamMember } from '~/composables/useProjects'

const route = useRoute()
const { getProjectBySlug, getProjectsByFormation } = useProjects()
const { setProjectSeo } = useSeo()

const project = await getProjectBySlug(route.params.slug as string)
const selectedItem = ref<ProjectGalleryItem | null>(null)

if (!project) {
  throw createError({ statusCode: 404, statusMessage: 'Projet non trouvé' })
}

const allRelated = await getProjectsByFormation(project.formation)
const relatedProjects = allRelated
  .filter(p => p.slug !== project.slug)
  .slice(0, 3)

// Set SEO using the composable
await setProjectSeo({
  title: project.title,
  description: project.description,
  metaTitle: project.metaTitle,
  metaDescription: project.metaDescription,
  keywords: project.keywords,
  ogImage: project.ogImage,
  image: project.image
})

// Split team members
const internalMembers = computed(() => project.team?.filter(m => m.userId) || [])
const externalMembers = computed(() => project.team?.filter(m => !m.userId) || [])

// Split gallery items
const galleryMedia = computed(() => project.gallery?.filter(item => item.type === 'image' || item.type === 'video') || [])
const galleryDocs = computed(() => project.gallery?.filter(item => item.type === 'document') || [])

const learnMoreLink = computed(() => {
  return project.ctaUrl || '/formations'
})

const ctaButtonText = computed(() => {
  if (project.ctaUrl === '/formations') return 'VOIR NOS BACHELORS'
  return project.formation || 'En savoir plus'
})

interface CtaVariation {
  title: string
  text: string
}

// Dynamic CTA Content (A/B Testing)
const ctaVariations: [CtaVariation, CtaVariation] = [
  {
    title: 'ET SI C’ÉTAIT VOUS ?',
    text: 'De l’idée à la réalisation concrète, nos étudiants apprennent à transformer leur passion en expertise. Découvrez le cursus qui a rendu ce projet possible :'
  },
  {
    title: "PASSEZ DE L'AUTRE CÔTÉ",
    text: 'Vous imaginez déjà votre propre univers ? Apprenez les méthodes et les outils qui ont permis à nos étudiants d’atteindre ce niveau :'
  }
]

const selectedCtaIndex = ref(Math.floor(Math.random() * ctaVariations.length))
const selectedCta = computed<CtaVariation>(() => {
  return ctaVariations[selectedCtaIndex.value] ?? ctaVariations[0]
})
</script>

<template>
  <div class="relative text-foreground min-h-screen font-sans transition-colors duration-300 bg-background">
    <!-- Hero Section Container -->
    <div class="relative z-10">
      <UiPageHero 
        :image="project.image || project.backgroundImage"
        :title="project.title"
        height="screen"
        align="center"
      >
        <template #badge>
          <div class="flex flex-wrap items-center justify-center gap-3">
            <span class="px-4 py-1.5 bg-oxo-red text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-full backdrop-blur-md shadow-xl border border-white/10">
              {{ project.formation }}
            </span>
            <span class="px-4 py-1.5 bg-oxo-card-bg/20 text-foreground/90 text-[10px] font-black uppercase tracking-[0.2em] rounded-full backdrop-blur-md border border-oxo-border">
              <template v-if="project.realizationDate">
                {{ new Date(project.realizationDate).getFullYear() }} / 
              </template>
              Promotion<span v-if="project.promotionName"> - {{ project.promotionName }}</span>
            </span>
          </div>
        </template>

        <template #info>
          <div class="flex flex-wrap items-center justify-center gap-8 text-white text-xs font-bold uppercase tracking-widest">
            <div v-if="project.duration" class="flex items-center gap-2">
              <i class="far fa-clock text-oxo-red" aria-hidden="true"></i>
              <span>{{ project.duration }}</span>
            </div>
            <div v-if="project.type" class="flex items-center gap-2">
              <i class="fas fa-layer-group text-oxo-red" aria-hidden="true"></i>
              <span>{{ project.type }}</span>
            </div>
          </div>
        </template>

        <template #actions>
          <div class="flex flex-wrap items-center justify-center gap-4">
            <AppButton 
              v-if="project.video"
              @click="selectedItem = { type: 'video', url: project.video }"
              variant="secondary"
              size="md"
            >
              <template #icon>
                <i class="fas fa-play" aria-hidden="true"></i>
              </template>
              Regarder le Trailer
            </AppButton>
            
            <AppButton to="/projets" variant="outline" size="md">
              <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
              Retour au Hub
            </AppButton>
          </div>
        </template>
      </UiPageHero>
    </div>

    <!-- Main Content with atmosphere -->
    <div class="relative overflow-hidden">
      <!-- Section Ambiance Background -->
      <div v-if="project.backgroundImage" class="absolute inset-0 z-0 pointer-events-none">
         <div class="absolute inset-0 bg-background opacity-90 dark:opacity-60 z-10"></div>
         <NuxtImg loading="lazy"
            :src="project.backgroundImage"
            alt=""
            class="w-full h-full object-cover opacity-80 blur-xl scale-105"
            sizes="100vw"
         />
      </div>

      <section class="container mx-auto px-container-px py-section-py relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-20">
          
          <div class="lg:col-span-8 space-y-24">
            <article class="space-y-8 max-w-3xl">
              <header class="flex items-center gap-4">
                <h2 class="font-display font-black text-3xl uppercase text-foreground">Le Projet</h2>
                <div class="flex-1 h-px bg-oxo-border"></div>
              </header>
              <p class="text-xl md:text-2xl text-foreground-dim leading-relaxed font-light">
                {{ project.description }}
              </p>
            </article>

            <section v-if="galleryMedia.length > 0" class="space-y-12" aria-label="Galerie média">
              <header class="flex items-center gap-4">
                <h2 class="font-display font-black text-3xl uppercase text-foreground">Galerie</h2>
                <div class="flex-1 h-px bg-oxo-border"></div>
              </header>
              
              <ul class="grid grid-cols-1 md:grid-cols-2 gap-6 list-none p-0">
                <li 
                  v-for="(item, idx) in galleryMedia" 
                  :key="idx"
                  class="group relative overflow-hidden rounded-2xl bg-oxo-card-bg/20 border border-oxo-border cursor-pointer aspect-video"
                  @click="selectedItem = item"
                >
                  <NuxtImg loading="lazy"
                    :src="item.type === 'video' ? project.image : item.url"
                    :alt="project.title"
                    class="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
                    sizes="50vw md:33vw"
                  />
                  
                  <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center gap-4 backdrop-blur-xs">
                    <div class="w-12 h-12 rounded-full bg-oxo-red flex items-center justify-center scale-50 group-hover:scale-100 transition-transform duration-500">
                      <i :class="['fas', item.type === 'video' ? 'fa-play' : 'fa-expand', 'text-white']" aria-hidden="true"></i>
                    </div>
                    <span class="text-[10px] font-black uppercase tracking-[0.2em]">{{ item.type === 'video' ? 'Lire la vidéo' : 'Agrandir l\'image' }}</span>
                  </div>

                  <div v-if="item.type === 'video'" class="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center group-hover:opacity-0 transition-opacity">
                     <i class="fas fa-play text-white text-xs translate-x-0.5" aria-hidden="true"></i>
                  </div>
                </li>
              </ul>
            </section>

            <section v-if="galleryDocs.length > 0" class="space-y-8" aria-label="Documents téléchargeables">
              <header class="flex items-center gap-4">
                <h2 class="font-display font-black text-3xl uppercase text-foreground">Documents & Ressources</h2>
                <div class="flex-1 h-px bg-oxo-border"></div>
              </header>
              
              <ul class="grid grid-cols-1 sm:grid-cols-2 gap-4 list-none p-0">
                 <li v-for="(doc, idx) in galleryDocs" :key="idx">
                    <a 
                      :href="doc.url"
                      target="_blank"
                      class="flex items-center gap-4 p-6 bg-oxo-card-bg/20 border border-oxo-border rounded-2xl hover:bg-oxo-card-bg/40 hover:border-oxo-red/30 transition-all group"
                     >
                        <div class="w-12 h-12 rounded-xl bg-oxo-red/10 group-hover:bg-oxo-red/20 flex items-center justify-center transition-colors">
                           <i class="far fa-file-pdf text-xl text-oxo-red" aria-hidden="true"></i>
                        </div>
                        <div class="flex-1 min-w-0">
                           <p class="text-xs font-bold truncate">{{ doc.description || doc.url.split('/').pop() || 'Document' }}</p>
                           <p class="text--[9px] font-black uppercase tracking-widest text-white/30">Consulter le document</p>
                        </div>
                        <i class="fas fa-download text-white/20 group-hover:text-white/60 transition-colors" aria-hidden="true"></i>
                     </a>
                 </li>
              </ul>
            </section>

            <section v-if="(project.team?.length ?? 0) > 0" class="space-y-16" aria-label="L'équipe du projet">
              <div v-if="internalMembers.length > 0" class="space-y-10">
                <header class="flex items-center gap-4">
                  <h2 class="font-display font-black text-3xl uppercase text-foreground">L'Équipe Étudiante</h2>
                  <div class="flex-1 h-px bg-oxo-border"></div>
                </header>
                
                <ul class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 list-none p-0">
                  <li v-for="member in internalMembers" :key="member.name">
                    <article class="group relative space-y-4">
                      <div class="aspect-square rounded-2xl overflow-hidden bg-oxo-card-bg border border-oxo-border relative">
                         <NuxtImg loading="lazy"
                          :src="member.userProImage || '/images/placeholders/avatar.jpg'"
                          :alt="member.name"
                          class="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                          sizes="50vw sm:33vw md:25vw"
                         />
                         <div class="absolute bottom-3 left-3">
                            <span class="px-2 py-1 bg-oxo-red/90 text-white text-[10px] font-black uppercase tracking-widest rounded-sm backdrop-blur-md border border-white/10 shadow-lg">
                               {{ member.role }}
                            </span>
                         </div>
                      </div>
                      <div class="flex items-center justify-between gap-4">
                        <div>
                          <h3 class="font-bold text-lg text-foreground group-hover:text-oxo-red transition-colors capitalize">
                            {{ member.name }}
                          </h3>
                          <p v-if="member.pseudo && member.pseudo !== member.name" class="text-xs text-foreground-dim font-medium">@{{ member.pseudo }}</p>
                        </div>
                        <a v-if="member.link" :href="member.link" target="_blank" class="w-8 h-8 rounded-full bg-oxo-card-bg/40 border border-oxo-border hover:bg-oxo-red text-foreground hover:text-white flex items-center justify-center transition-all">
                           <i v-if="member.link.includes('linkedin')" class="fab fa-linkedin-in text-xs" aria-hidden="true"></i>
                           <i v-else class="fas fa-link text-xs" aria-hidden="true"></i>
                        </a>
                      </div>
                    </article>
                  </li>
                </ul>
              </div>

              <!-- Fiche Technique (Mobile Only - Before Contributions as requested) -->
              <ProjectSpecs 
                v-if="project" 
                :project="project" 
                :gallery-docs="galleryDocs" 
                class="lg:hidden" 
              />

              <!-- Collaboration Externe Refined Display -->
              <div v-if="externalMembers.length > 0" class="relative group/ext overflow-hidden rounded-[2.5rem] bg-linear-to-br from-oxo-card-bg/60 to-background/40 backdrop-blur-3xl border border-oxo-border transition-all duration-700 hover:border-oxo-red/20 shadow-2xl p-8 md:p-14">
                <!-- Background Decorative Quote -->
                <div class="absolute -top-12 -right-12 text-oxo-red opacity-[0.03] dark:text-white dark:opacity-[0.03] group-hover/ext:opacity-[0.05] transition-opacity duration-1000 pointer-events-none">
                   <i class="fas fa-quote-right text-[15rem] md:text-[20rem]" aria-hidden="true"></i>
                </div>

                <div class="relative z-10 space-y-10 md:space-y-12">
                  <header class="space-y-4 text-center md:text-left">
                    <div class="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-oxo-red/10 border border-oxo-red/20 text-oxo-red text-[10px] font-black uppercase tracking-[0.3em] mb-2 selection:bg-oxo-red selection:text-white">
                      <span class="w-1.5 h-1.5 rounded-full bg-oxo-red shadow-[0_0_8px_rgba(184,46,26,0.5)] animate-pulse"></span>
                      Partenariat & Collaboration
                    </div>
                    <h3 class="text-2xl md:text-3xl font-display font-black uppercase text-foreground leading-tight">
                      Ils ont contribué <span class="text-transparent bg-clip-text bg-linear-to-r from-oxo-red to-orange-500">au succès</span> du projet
                    </h3>
                    <div class="relative pl-6 py-1">
                      <div class="absolute inset-y-0 left-0 w-0.5 bg-linear-to-b from-oxo-red/40 via-oxo-red/20 to-transparent rounded-full"></div>
                      <p class="text-base md:text-lg text-foreground-dim font-light leading-relaxed italic">
                         " Nous adressons nos vifs remerciements aux intervenants et contributeurs externes pour leur expertise et leur soutien précieux auprès de nos étudiants. "
                      </p>
                    </div>
                  </header>

                  <ul class="grid grid-cols-1 lg:grid-cols-2 gap-5 list-none p-0">
                     <li v-for="m in externalMembers" :key="m.name" class="group/card flex flex-col h-full">
                        <div class="flex-1 flex items-center justify-between p-6 bg-background/5 hover:bg-background/10 border border-oxo-border/50 hover:border-oxo-red/40 rounded-4xl transition-all duration-500 shadow-lg group-hover/card:-translate-y-1">
                          <div class="flex flex-col gap-2 min-w-0 flex-1 pr-4">
                            <div class="flex items-center gap-2 flex-wrap">
                                <span class="text-base font-bold text-foreground group-hover/card:text-oxo-red transition-colors capitalize truncate">{{ m.name }}</span>
                                <span v-if="m.pseudo && m.pseudo !== m.name" class="px-2 py-0.5 rounded-sm bg-oxo-red/10 text-[10px] text-oxo-red font-black uppercase tracking-widest border border-oxo-red/20">@{{ m.pseudo }}</span>
                            </div>
                            <div class="flex items-center gap-2">
                              <span class="text-[10px] font-black uppercase tracking-[0.2em] text-oxo-red/80">{{ m.role }}</span>
                            </div>
                          </div>
                          
                          <a v-if="m.link" 
                             :href="m.link" 
                             target="_blank" 
                             class="shrink-0 w-11 h-11 rounded-2xl bg-oxo-card-bg border border-oxo-border/50 flex items-center justify-center text-foreground-dim hover:text-white hover:bg-oxo-red hover:border-oxo-red transition-all duration-300 shadow-md active:scale-90 group/btn"
                             aria-label="Voir le profil"
                          >
                             <i v-if="m.link.includes('linkedin')" class="fab fa-linkedin-in text-xs md:text-sm" aria-hidden="true"></i>
                             <i v-else class="fas fa-link text-xs md:text-sm" aria-hidden="true"></i>
                          </a>
                        </div>
                     </li>
                  </ul>
                </div>
              </div>
            </section>
          </div>

          <!-- Sidebar specifications -->
          <aside class="lg:col-span-4 h-fit">
            <div class="sticky top-32 space-y-6">
              <!-- Fiche Technique (Desktop Only - In Sidebar) -->
              <ProjectSpecs 
                v-if="project" 
                :project="project" 
                :gallery-docs="galleryDocs" 
                class="hidden lg:block" 
              />

              <!-- Action CTA -->
              <section class="relative group/cta overflow-hidden rounded-[2.5rem] bg-linear-to-br from-oxo-red to-orange-600 p-8 space-y-4 shadow-2xl transition-all duration-500 hover:shadow-oxo-red/20 hover:-translate-y-1">
                 <!-- Background Shine Effect -->
                 <div class="absolute inset-0 bg-white/20 -translate-x-full group-hover/cta:translate-x-full transition-transform duration-1000 skew-x-[-20deg]"></div>
                 
                 <div class="relative z-10 space-y-4">
                   <h2 class="font-black uppercase tracking-widest text-[10px] text-white/90 drop-shadow-xs">{{ selectedCta.title }}</h2>
                   <p class="text-sm md:text-base font-bold leading-relaxed text-white drop-shadow-md">{{ selectedCta.text }}</p>
                    <AppButton 
                      :to="learnMoreLink" 
                      variant="primary" 
                      class="w-full mt-4 bg-black! text-white! text-[10px] font-black uppercase tracking-widest hover:bg-white! hover:text-black! transition-all shadow-2xl active:scale-95"
                    >
                      {{ ctaButtonText }}
                    </AppButton>
                 </div>
              </section>
            </div>
          </aside>
        </div>
      </section>
    </div>

    <!-- Modal Transition -->
    <Transition name="modal">
      <div 
        v-if="selectedItem" 
        class="fixed inset-0 z-50 flex items-center justify-center p-6 bg-background/95 backdrop-blur-xl"
        @click.self="selectedItem = null"
      >
        <button 
          @click="selectedItem = null"
          class="absolute top-8 right-8 w-14 h-14 rounded-full bg-white/5 text-white flex items-center justify-center hover:bg-oxo-red transition-all group z-60"
        >
          <i class="fas fa-times text-xl group-hover:rotate-90 transition-transform" aria-hidden="true"></i>
        </button>

        <div class="relative w-full max-w-6xl max-h-[85vh] flex items-center justify-center">
          <div v-if="selectedItem.type === 'video'" class="w-full aspect-video rounded-3xl overflow-hidden shadow-2xl border border-white/10">
            <UiVideoPlayer 
              :src="selectedItem.url" 
              :autoplay="true"
            />
          </div>

          <img 
            v-else 
            :src="selectedItem.url" 
            :alt="project.title"
            class="max-w-full max-h-[80vh] object-contain rounded-3xl shadow-2xl border border-white/10"
          />
        </div>
      </div>
    </Transition>

    <!-- Global CTA Section -->
    <AppCTA 
      title="Prêt à créer votre"
      titleHighlight="PROPRE UNIVERS ?"
      subtitle="Rejoignez OXO pour apprendre les métiers du jeu vidéo et du web dans un environnement bienveillant et professionnalisant."
    />
  </div>
</template>

<style scoped>
.delay-300 { animation-delay: 0.3s; }

.modal-enter-active, .modal-leave-active {
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.modal-enter-from .max-w-6xl {
  transform: scale(0.9);
}
</style>
